import { userManager } from "../auth/oidc";
import type {
  Facets, Page, Problem, StreamSet, Stats, Tracklist, TrackPage, Release,
  Principal, Favorite, Playlist, PlaylistView, PlaylistBody, Shop,
  TestListView, Deck,
} from "./types";

const base = (import.meta.env.VITE_API_BASE as string) || "";

/** An error carrying what the API said, so a component can show it. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: Problem | null,
    message: string,
  ) {
    super(message);
  }
}

/**
 * One call. Attaches the bearer token, sends a JSON body where there is one,
 * and turns a problem+json answer into an ApiError the page can show. The
 * admin client and the catalogue client both go through here, so there is
 * one place that knows how the API says no.
 */
export async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const user = await userManager.getUser();
  const res = await fetch(base + path, {
    ...init,
    headers: {
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(user?.access_token ? { Authorization: `Bearer ${user.access_token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });

  if (!res.ok) {
    let problem: Problem | null = null;
    try {
      problem = (await res.json()) as Problem;
    } catch {
      // A response that is not problem+json is still a failure; the status is
      // the only thing worth reporting about it.
    }
    throw new ApiError(
      res.status,
      problem,
      problem?.detail || problem?.title || `HTTP ${res.status}`,
    );
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function get<T>(path: string, params?: URLSearchParams): Promise<T> {
  return call<T>(path + (params && [...params].length ? `?${params}` : ""));
}

const json = (method: string, body?: unknown): RequestInit =>
  body === undefined ? { method } : { method, body: JSON.stringify(body) };

const enc = encodeURIComponent;

export const api = {
  releases: (q: URLSearchParams) => get<Page>("/api/releases", q),
  trackSearch: (q: URLSearchParams) => get<TrackPage>("/api/tracks", q),
  /** One release, shaped exactly as it appears in a search result -- the track
   *  view holds tracks, not releases, so anything wanting a whole card fetches
   *  it by key rather than hunting the list it is not in. */
  release: (key: string) => get<Release>(`/api/releases/${enc(key)}`),
  facets: (q: URLSearchParams) => get<Facets>("/api/facets", q),
  tracks: (key: string) => get<Tracklist>(`/api/releases/${enc(key)}/tracks`),
  /** What a release can play. `tracks` is always a list here: the server
   *  once answered null for a release with nothing cached, and a null is a
   *  crash in every player that reads it. */
  streams: (key: string) => get<StreamSet>(`/api/releases/${enc(key)}/streams`)
    .then((s) => ({ ...s, tracks: s.tracks ?? [] })),
  shops: () => get<Shop[]>("/api/shops"),
  stats: () => get<Stats>("/api/stats"),

  /** Who I am. Also how the server learns I was here. */
  me: () => get<Principal>("/api/me"),

  // --- what I own ----------------------------------------------------------
  favorites: () => get<Favorite[]>("/api/me/favorites"),
  favorite: (key: string, on: boolean) =>
    call<void>(`/api/me/favorites/${enc(key)}`, json(on ? "PUT" : "DELETE")),

  playlists: () => get<Playlist[]>("/api/me/playlists"),
  createPlaylist: (b: PlaylistBody) =>
    call<{ id: number }>("/api/me/playlists", json("POST", b)),
  playlist: (id: number) => get<PlaylistView>(`/api/playlists/${id}`),
  updatePlaylist: (id: number, b: PlaylistBody) =>
    call<PlaylistView>(`/api/playlists/${id}`, json("PUT", b)),
  deletePlaylist: (id: number) => call<void>(`/api/playlists/${id}`, json("DELETE")),
  newShareToken: (id: number) =>
    call<{ shareToken: string }>(`/api/playlists/${id}/share-token`, json("POST")),
  /** One track, or -- with no pos -- every playable track of the release. */
  addToPlaylist: (id: number, release: string, pos?: number) =>
    call<PlaylistView & { added: number }>(`/api/playlists/${id}/items`,
      json("POST", pos === undefined ? { release } : { release, pos })),
  removeFromPlaylist: (id: number, item: number) =>
    call<void>(`/api/playlists/${id}/items/${item}`, json("DELETE")),
  reorderPlaylist: (id: number, items: number[]) =>
    call<void>(`/api/playlists/${id}/order`, json("PUT", { items })),
  sharedPlaylist: (token: string) => get<PlaylistView>(`/api/shared/${enc(token)}`),

  // --- the test list and the decks ------------------------------------------
  testList: () => get<TestListView>("/api/me/testlist"),
  addToTestList: (release: string, pos: number) =>
    call<TestListView & { added: boolean }>("/api/me/testlist", json("POST", { release, pos })),
  removeFromTestList: (item: number) => call<void>(`/api/me/testlist/${item}`, json("DELETE")),
  /** 404 (type `deck-pending`) until the job has made it; 409 when it gave up. */
  deck: (release: string, pos: number) => get<Deck>(`/api/decks/${enc(release)}/${pos}`),
  retryDeck: (release: string, pos: number) =>
    call<void>(`/api/decks/${enc(release)}/${pos}/retry`, json("POST")),
};
